// Shared LLM-payload assembler for every agent that reasons over exam materials (MS-generation,
// GS-generation, grading, …). Agents pass only the categories they need, as a { boxes, text } section
// per category — typically the task's REAL bounding boxes plus, where applicable, a canonical text
// string (for AI-generated content that has no box). The assembler returns a single ordered list of
// chat content parts where EACH category is introduced by a label, so the model always knows what an
// image/text belongs to. All assembly lives here — callers don't reshape boxes.
//
// Within a category, boxes are emitted chronologically by content type (the SAME uniform rule
// everywhere):
//   - 'text'  box -> its text (we trust the OCR transcription)
//   - 'image' box -> its image
//   - 'both'  box -> its image, then its text right below
// Each box's `isHandwritten` is carried but NOT acted on by the default rule (so we can later choose
// to also send the image for handwritten text if the transcription proves unreliable).
//
// A section's `text` (canonical/generated content, e.g. an AI master solution that was never OCR'd)
// is emitted only when no box already contributed text, so it never duplicates box OCR text.

import { getByID } from '@adornis/baseql/operations/mongo.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import { normalizeContentType, type BoundingBoxContentType } from '../../../shared/bounding-box-content.js';
import { calcTotalBase64Bytes, fileToBase64URL, resizeBase64URLToBase64URL } from '../../utils/transform-file.js';
import { logger } from '@adornis/base/logging.js';

export type AgentPayloadPart =
  | { type: 'text'; text: string }
  | { type: 'image_url'; image_url: { url: string; detail: 'low' | 'high' } };

export interface PayloadBox {
  contentType?: BoundingBoxContentType | null;
  ocrExtractedText?: string | null;
  imageId?: string | null;
  /** Carried for future tuning; the current rule does NOT change behavior based on it. */
  isHandwritten?: boolean | null;
}

export interface PayloadSection {
  /** The real bounding boxes for this category, rendered per content type (text / image / both). */
  boxes?: PayloadBox[] | null;
  /**
   * Canonical text not represented by any box — e.g. an AI-generated master solution / grading scheme,
   * whose text lives in a string field rather than an OCR'd box. Emitted only when no box already
   * contributed text, so it never duplicates box OCR text.
   */
  text?: string | null;
  maxPoints?: number | null; // for grading scheme sections, to carry the max points
}

export interface AssembleAgentPayloadInput {
  /** The task description — what the student was asked. */
  taskDescription?: PayloadSection | null;
  /** Supporting material handed to the student (formula sheets, diagrams, shared tables). */
  additionalInformation?: PayloadSection | null;
  /** The master solution. */
  masterSolution?: PayloadSection | null;
  /** The grading scheme. */
  gradingScheme?: PayloadSection | null;
  /** Grammar/spelling-only grading criteria for this task, graded SEPARATELY from content (see gradingScheme). */
  languageUseScheme?: PayloadSection | null;
  /** The student's answer. */
  studentAnswer?: PayloadSection | null;
  /** When refining MS/GS, label them as the "current" ones being revised. */
  current?: boolean;
  /** Image detail level (default 'low' to keep payloads small; grading may want 'high'). */
  imageDetail?: 'low' | 'high';
}

function textPart(text: string): AgentPayloadPart {
  return { type: 'text', text };
}

function imagePart(url: string, detail: 'low' | 'high'): AgentPayloadPart {
  return { type: 'image_url', image_url: { url, detail } };
}

async function resolveImageUrl(imageId: string, cache: Map<string, string | null>): Promise<string | null> {
  if (cache.has(imageId)) return cache.get(imageId) ?? null;
  let url: string | null = null;
  try {
    const image = await getByID(ImageFile, imageId)(ImageFile.allFields);
    if (!image) {
      logger.warn({ imageId }, '[AgentPayload] imageId referenced by a box has no ImageFile document — dropped');
    } else {
      const file =
        image instanceof ImageFile
          ? image
          : new ImageFile({
              ...(image as any),
              meta: (image as any)?.meta ?? {},
              createdAt: (image as any)?.createdAt ?? new Date(),
              _id: (image as any)?._id ?? imageId,
              _class: (image as any)?._class ?? ImageFile._class,
            });
      url = await fileToBase64URL(file);
    }
  } catch (err) {
    // Swallowed rather than thrown so one bad image doesn't fail the whole payload — but logged, since a
    // silent null here means the model sees no image for that box with nothing to explain why.
    logger.warn({ err, imageId }, '[AgentPayload] failed to resolve image for payload — dropped');
    url = null;
  }
  cache.set(imageId, url);
  return url;
}

async function renderSection(
  label: string,
  section: PayloadSection | null | undefined,
  detail: 'low' | 'high',
  cache: Map<string, string | null>,
): Promise<AgentPayloadPart[]> {
  const boxes = Array.isArray(section?.boxes) ? section!.boxes! : [];
  const canonicalText = String(section?.text ?? '').trim();

  const inner: AgentPayloadPart[] = [];
  let boxTextEmitted = false;
  for (const box of boxes) {
    const type = normalizeContentType(box?.contentType);
    const text = String(box?.ocrExtractedText ?? '').trim();
    const imageId = String(box?.imageId ?? '').trim();
    // box.isHandwritten is available here for future tuning; the current rule trusts the OCR text.

    if ((type === 'image' || type === 'both') && imageId) {
      const url = await resolveImageUrl(imageId, cache);
      if (url) inner.push(imagePart(url, detail));
    }
    if ((type === 'text' || type === 'both') && text) {
      inner.push(textPart(text));
      boxTextEmitted = true;
    }
    if (section?.maxPoints != null) {
      inner.push(textPart(`The maximum points for this task are: ${section.maxPoints}`));
    }
  }

  // Canonical/generated text that no box already carried (e.g. an AI-generated solution).
  if (!boxTextEmitted && canonicalText) {
    inner.push(textPart(canonicalText));
  }

  if (inner.length === 0) return [];
  return [textPart(label), ...inner];
}

/**
 * Single disclaimer covering both the TASK DESCRIPTION and ADDITIONAL INFORMATION sections: their images
 * are crops of the blank exam and may, for processing reasons, also carry the student's handwriting.
 * Emitted once per payload (not per task) — the hierarchy assembler emits it itself and tells
 * assembleAgentPayload to skip it so it isn't repeated for every task.
 */
export const STUDENT_ANSWER_IMAGE_DISCLAIMER =
  "IMPORTANT DISCLAIMER: For processing reasons, the images in the TASK DESCRIPTION and ADDITIONAL INFORMATION sections may also contain the student's handwritten answers. Ignore any such handwriting — read those sections only as the printed task / supporting material.";

/**
 * Assemble the ordered, labeled content parts for an LLM user message. Returns [] when nothing was
 * provided. Callers spread the result into the user message content (optionally after a short
 * instruction text part). Pass `{ includeDisclaimer: false }` when the caller emits the shared
 * student-answer-image disclaimer itself (e.g. assembleHierarchyPayload, once for the whole tree).
 */
export async function assembleAgentPayload(
  input: AssembleAgentPayloadInput,
  opts?: { includeDisclaimer?: boolean },
): Promise<AgentPayloadPart[]> {
  const detail = input.imageDetail ?? 'low';
  const cache = new Map<string, string | null>();
  const parts: AgentPayloadPart[] = [];

  const section = (label: string, sec: PayloadSection | null | undefined) => renderSection(label, sec, detail, cache);

  parts.push(...(await section('### TASK DESCRIPTION (what the student was asked):', input.taskDescription)));
  parts.push(
    ...(await section(
      '### ADDITIONAL INFORMATION (supporting material given to the student):',
      input.additionalInformation,
    )),
  );
  parts.push(
    ...(await section(
      input.current ? '### CURRENT MASTER SOLUTION (to be revised):' : '### MASTER SOLUTION:',
      input.masterSolution,
    )),
  );
  parts.push(
    ...(await section(
      input.current ? '### CURRENT GRADING SCHEME (to be revised):' : '### GRADING SCHEME:',
      input.gradingScheme,
    )),
  );
  parts.push(
    ...(await section(
      '### GRAMMAR GRADING CRITERIA (grade grammar/spelling SEPARATELY from content, see instructions):',
      input.languageUseScheme,
    )),
  );
  parts.push(...(await section("### STUDENT'S ANSWER (to be graded):", input.studentAnswer)));

  // Prepend the single disclaimer once, ahead of all sections (only when there is content to disclaim).
  if (opts?.includeDisclaimer !== false && parts.length > 0) {
    parts.unshift(textPart(STUDENT_ANSWER_IMAGE_DISCLAIMER));
  }

  logger.info(
    `[AgentPayload] assembled ${parts.length} part(s), ${parts.filter(p => p.type === 'image_url').length} image(s)`,
  );
  return parts;
}

/** Wrap a list of image ids as image boxes (for sources that only expose ids, e.g. additional info). */
export function imageIdsAsBoxes(imageIds: Array<string | null | undefined> | null | undefined): PayloadBox[] {
  return (Array.isArray(imageIds) ? imageIds : [])
    .map(id => String(id ?? '').trim())
    .filter(Boolean)
    .map(imageId => ({ contentType: 'image' as const, imageId }));
}

export interface HierarchyTaskInput extends AssembleAgentPayloadInput {
  /** Stable id used in the section header (and to correlate with the requested grading targets). */
  id: string;
  /** e.g. "MAIN" / "SUB". */
  hierarchy?: string;
  taskType?: string;
  maxPoints?: number;
  /** Optional instruction line emitted immediately under this task's header (e.g. a required-sum reminder). */
  note?: string;
}

/**
 * Assemble a payload for a nested task hierarchy (e.g. a main task plus its subtasks for grading) by
 * delegating each task to assembleAgentPayload and prefixing it with a header that names the task,
 * so the model knows which materials belong to which task. Tasks with no materials still emit their
 * header so the structure stays visible.
 */
export async function assembleHierarchyPayload(
  tasks: HierarchyTaskInput[],
  options?: { imageDetail?: 'low' | 'high' },
): Promise<AgentPayloadPart[]> {
  const taskList = Array.isArray(tasks) ? tasks : [];
  const parts: AgentPayloadPart[] = [];
  // Emit the student-answer-image disclaimer ONCE for the whole tree, not per task.
  if (taskList.length > 0) parts.push(textPart(STUDENT_ANSWER_IMAGE_DISCLAIMER));
  for (const task of taskList) {
    const meta = [
      task.hierarchy ? task.hierarchy : null,
      task.taskType ? `type: ${task.taskType}` : null,
      typeof task.maxPoints === 'number' ? `max points: ${task.maxPoints}` : null,
    ]
      .filter(Boolean)
      .join(', ');
    parts.push(textPart(`\n========== TASK "${task.id}"${meta ? ` (${meta})` : ''} ==========`));
    if (task.note && task.note.trim()) parts.push(textPart(task.note));
    parts.push(
      ...(await assembleAgentPayload(
        { ...task, imageDetail: options?.imageDetail ?? task.imageDetail },
        { includeDisclaimer: false },
      )),
    );
  }
  return parts;
}

/**
 * Resize the image parts in a payload if their combined size exceeds the byte limit. Text parts are
 * untouched. Returns a new array; the caller passes it to the LLM for this single call.
 */
export async function clampPayloadToByteLimit(
  parts: AgentPayloadPart[],
  limitBytes: number,
): Promise<AgentPayloadPart[]> {
  const imageUrls = parts.filter((p): p is Extract<AgentPayloadPart, { type: 'image_url' }> => p.type === 'image_url');
  if (imageUrls.length === 0) return parts;
  if (calcTotalBase64Bytes(imageUrls.map(p => p.image_url.url)) <= limitBytes) return parts;

  return Promise.all(
    parts.map(async part =>
      part.type === 'image_url'
        ? { ...part, image_url: { ...part.image_url, url: await resizeBase64URLToBase64URL(part.image_url.url) } }
        : part,
    ),
  );
}
